import { IonHeader, IonContent, IonIcon, IonItem, IonPage, IonInput, IonGrid, IonRow, IonCol, IonToolbar, IonButtons, useIonModal, IonButton, IonCard, IonCardContent, IonLabel, IonCardHeader, IonCardSubtitle, IonTitle, IonThumbnail, IonNote, IonSelect, SelectCustomEvent, IonSelectOption, IonTextarea, TextareaCustomEvent, useIonAlert } from "@ionic/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import './ReceivePage.css';
import { arrowBack, arrowBackOutline, closeOutline, qrCodeOutline, shareOutline } from "ionicons/icons";
import { useAppDispatch, useAppSelector } from '../../services/redux/hook';
import { PayStellarUri } from "../../services/stellar/stellar-uri/pay-stellar-uri";
import { Asset, PaymentLinkRequest, WalletInfoState } from "../../types/types";
import { Share } from "@capacitor/share";
import { useAccountDetailsQuery } from "../../services/api/stellar";
import useCryptoMode from "../../hooks/useCryptoMode";
import StellarPayQRModal from "./ReceiveStellarPayQrModal";
import { ebioroApi, useGetUserInfoQuery, useSignUriMutation } from "../../services/api/ebioro/ebioro";
import { parseStellarUri } from "../../services/stellar/stellar-uri/parse";
import { Networks } from "@stellar/stellar-sdk";
import * as Sentry from '@sentry/react';
import { ok, err, Result } from 'neverthrow'
import { Provider } from "react-redux";
import { store } from "../../services/redux/store";
import { closeModal } from "../../services/redux/slices";
import { useForm } from "react-hook-form";


const ebioroAuthDomain = import.meta.env.VITE_API_AUTH_DOMAIN || '';
const isTestNet = import.meta.env.VITE_isTestnet || '';

const Page: React.FC<any> = () => {

    const { t, i18n } = useTranslation();
    const { cryptoMode } = useCryptoMode();
    const dispatch = useAppDispatch();

    const walletInfo: WalletInfoState = useAppSelector((state) => state.walletInfo);
    const selectedAsset: Asset = useAppSelector((state) => state.selectedAsset);
    const [toReceiveAsset, setToReceiveAsset] = useState<Asset>(selectedAsset);
    const supportedAssets: Asset[] = useAppSelector((state) => state.supportedAssets.supportedAssets);
    const { data: accountDetails } = useAccountDetailsQuery(walletInfo.account_key, { skip: (!walletInfo.has_key) });
    const [assetsToReceiveArray, setAssetsToReceiveArray] = useState<Asset[]>([]);
    const { data: userInfo } = useGetUserInfoQuery({});

    const initialPayUri = PayStellarUri.forDestination(walletInfo?.account_key as string);
    initialPayUri.amount = '0';
    initialPayUri.originDomain = ebioroAuthDomain;
    initialPayUri.networkPassphrase = isTestNet === 'true' ? encodeURIComponent(Networks.TESTNET) : encodeURIComponent(Networks.PUBLIC);

    if (toReceiveAsset.identifier != 'native') {
        initialPayUri.assetCode = toReceiveAsset.identifier.split(":")[0];
        initialPayUri.assetIssuer = toReceiveAsset.identifier.split(':')[1];
    }

    const [payUri, setPayUri] = useState<PayStellarUri>(initialPayUri);
    const [signUri, result] = useSignUriMutation();
    const [present] = useIonAlert();
    const nav = document.querySelector("ion-nav");

    const {
        handleSubmit,
        setValue,
        register,
        trigger,
        watch,
        formState: { errors, isValid }
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            amount: '',
            message: ''
        }
    });

    const amount = watch('amount');
    const message = watch('message');

    const close = () => {

        dispatch(closeModal());

    }

    const presentErrorAlert = (message: any) => {

        present({
            header: 'Error',
            message: `${message}`,
            buttons: [
                {
                    text: 'Close',
                    role: 'cancel',
                    handler: () => {
                        return
                    }
                }
            ],

        })

    }

    const [presentQRModal, dismissAssetModal] = useIonModal(
        <StellarPayQRModal
            qrValue={payUri.toString()}
        />);


    const updateUriWithAmount = (value: string | undefined | null) => {
        const updatedUri = payUri.clone();

        if (value) {
            if (value.indexOf(',') > -1) {
                value = value.replace(',', '.');
            }

            const parsedAmount = parseFloat(value).toFixed(2);

            if (parsedAmount && !isNaN(+parsedAmount)) {
                updatedUri.amount = parsedAmount;
                setPayUri(updatedUri);
            } else {
                updatedUri.amount = '0';
                setPayUri(updatedUri);
            }
        } else {
            updatedUri.amount = '0'; // Ensure amount is reset to 0 if value is null or undefined
            setPayUri(updatedUri);
        }
    }

    const updateUriWithMessage = (messageValue: string | undefined | null) => {

        if (messageValue) {

            const updatedUri = payUri.clone();
            updatedUri.msg = messageValue ? encodeURIComponent(messageValue) : undefined;
            setPayUri(updatedUri)

        }
    }

    const openStellarPayQRModal = async () => {

        const result = await signPayURi();

        if (result.isErr()) {

            presentErrorAlert(result.error.message);

            return;
        }

        const signedUri = parseStellarUri(result.value);

        if (signedUri instanceof PayStellarUri) {
            setPayUri(signedUri)
        }

        presentQRModal({
            initialBreakpoint: 0.70,
            breakpoints: [0, 0.70, 0.90, 1],
            onDidDismiss: (() => (dismissAssetModal()))
        })

    }

    const handleShareStellarPayUri = async () => {
        try {
            // Get payment link from server using the existing API
            const assetIssuer = toReceiveAsset.identifier === 'native' ? '' : toReceiveAsset.identifier.split(':')[1];
            
            const paymentLinkRequest: PaymentLinkRequest = {
                paymentLink: userInfo?.paymentId as string,
                amount: amount,
                asset_code: toReceiveAsset.code,
                asset_issuer: assetIssuer,
                message: message || ''
            };

            // Use the existing RTK Query endpoint correctly
            const result = await ebioroApi.endpoints.getPaymentLink.initiate(paymentLinkRequest)(store.dispatch, store.getState, undefined);

            if ('error' in result || !result.data) {
                presentErrorAlert(t('stellarPay.page.SHARE-ERROR') as string);
                return;
            }
            
            // Create user-friendly share message with the server-generated payment link
            const shareText =  
                message && message.trim()
                    ? `${t('stellarPay.page.SHARE-REQUEST-WITH-MESSAGE', { 
                        amount: amount, 
                        asset: toReceiveAsset.display_code, 
                        message: message 
                      })}\n\n${result.data.paymentLink}`
                    : `${t('stellarPay.page.SHARE-REQUEST-NO-MESSAGE', { 
                        amount: amount, 
                        asset: toReceiveAsset.display_code
                      })}\n\n${result.data.paymentLink}`;

            await Share.share({
                title: t('stellarPay.page.SHARE-TITLE') as string,
                text: shareText,
            });

        } catch (error: any) {
            if (error.message !== "Share canceled") {
                Sentry.captureException(error);
                presentErrorAlert(t('stellarPay.page.SHARE-ERROR') as string);
            }
        }
    };

    const getAssetsToReceive = () => {

        const toReceiveAssets: Asset[] = [];

        supportedAssets
            .filter((asset: Asset) => {
                // Ensure the asset is either fiat or crypto based on the mode
                return (asset.type === 'fiat' || (cryptoMode && asset.type === 'crypto'))
            })
            .map((asset: Asset) => {
                // Only include assets that have a balance in the account
                if (accountDetails?.details.balances[asset.identifier]) {
                    toReceiveAssets.push(asset);
                }
            });

        setAssetsToReceiveArray(toReceiveAssets);

    }

    const handleSelectEvent = (assetCode: string) => {

        const selectedAsset = supportedAssets.find((asset: Asset) => {
            return asset.code === assetCode;
        });

        if (selectedAsset) {
            setToReceiveAsset(selectedAsset);

            const uri = payUri;

            if (selectedAsset.identifier === 'native') {
                uri.assetCode = undefined;
                uri.assetIssuer = undefined;
            }

            else {
                uri.assetCode = selectedAsset.identifier.split(":")[0];
                uri.assetIssuer = selectedAsset.identifier.split(":")[1];
            }

            setPayUri(uri);
        }
    }

    const signPayURi = async (): Promise<Result<string, Error>> => {
        
        const params = {
            uri: payUri.toString()
        }

        try {

            const response = await signUri(params).unwrap();
            const signedUri = parseStellarUri(response.uri);

            return ok(signedUri.toString())
        } catch (error) {

            Sentry.captureException(error);
            return err(new Error(`Error parsing payment uri`))
        }

    }

    useEffect(() => {
        getAssetsToReceive();
    }, [cryptoMode]);

    useEffect(() => {
        updateUriWithAmount(amount);
        updateUriWithMessage(message)
    }, [amount, message]);

    const addAmount = (value: string) => {
        if (value && value.toString().indexOf(',') > -1) {
            value = value.replace(/,/g, '.');
        }
        setValue('amount', value);
        trigger('amount');
        updateUriWithAmount(value); // Ensure payUri is updated when amount changes
    };

    const addMessage = (value: string) => {
        setValue('message', value);
        trigger('message');
    }


    return (
        <form onSubmit={handleSubmit(() => { })} style={{ padding: 18 }}>
            <IonPage>

                <IonHeader className="ion-no-border" >
                    <IonToolbar style={{ marginTop: "0%" }}>
                        <IonButtons slot='start'>
                            <IonButton color='primary' onClick={() => { nav?.pop() }}>
                                <IonIcon slot='start' icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>
                        <IonTitle>
                            {t('stellarPay.page.HEADER')}
                        </IonTitle>
                    </IonToolbar>


                </IonHeader>

                <IonContent scrollY={true}>

                    <IonCard style={{ marginTop: '10%' }}>
                        <IonCardHeader className="ion-text-start">
                            <IonCardSubtitle>
                                {t('stellarPay.page.AMOUNT-CARD-HEADER')}
                            </IonCardSubtitle>
                        </IonCardHeader>

                        <IonCardContent>

                            <IonGrid>
                                <IonRow>

                                    <IonCol size="7">
                                        <IonItem lines="none" color={'var(ion-background-color)'} >
                                            <IonThumbnail className="stellarPayAssetImg" slot="start">
                                                <img src={toReceiveAsset?.icon} />
                                            </IonThumbnail>
                                            {
                                                assetsToReceiveArray.length > 1
                                                    ? (
                                                        <IonSelect
                                                            interface="popover"
                                                            value={toReceiveAsset.code}
                                                            onIonChange={(e: SelectCustomEvent) => handleSelectEvent(e.detail.value)}
                                                        >
                                                            {
                                                                assetsToReceiveArray.map((asset) => {
                                                                    return (
                                                                        <IonSelectOption
                                                                            key={asset.code}
                                                                            value={asset.code}
                                                                        >
                                                                            {asset.code}
                                                                        </IonSelectOption>
                                                                    )
                                                                })
                                                            }

                                                        </IonSelect>)
                                                    : <h4> {toReceiveAsset?.display_code}</h4>
                                            }
                                        </IonItem>

                                    </IonCol>
                                    <IonCol >

                                        <IonInput
                                            key={amount}
                                            {...register('amount', {
                                                required: t('stellarPay.page.AMOUNT-REQUIRED') as string,
                                                validate: (value) => {
                                                    const parsedValue = parseFloat(value);
                                                    return (!isNaN(parsedValue) && parsedValue > 0) || (t('stellarPay.page.AMOUNT-MUST-BE-POSITIVE') as string);
                                                },
                                            })}
                                            mode="md"
                                            fill="outline"
                                            inputMode='decimal'
                                            clearOnEdit={true}
                                            autocapitalize="off"
                                            autocorrect="off"
                                            spellcheck={false}
                                            type='text'
                                            value={amount}
                                            debounce={950}
                                            onIonInput={
                                                (e: any) => {
                                                    addAmount(e.detail.value!);
                                                }}>

                                        </IonInput>

                                    </IonCol>

                                </IonRow>
                            </IonGrid>

                        </IonCardContent>
                    </IonCard>

                    <IonCard>
                        <IonCardHeader className="ion-text-start">
                            <IonCardSubtitle>
                                {t('stellarPay.page.MESSAGE-CARD-HEADER')}
                            </IonCardSubtitle>
                        </IonCardHeader>
                        <IonCardContent>
                            <IonTextarea
                                {...register('message', {
                                    required: t('stellarPay.page.MESSAGE-REQUIRED') as string,
                                })}
                                fill="outline"
                                placeholder={t('stellarPay.page.MESSAGE-CARD-PLACEHOLDER') as string}
                                mode="md"
                                counter={true}
                                clearOnEdit={true}
                                maxlength={35}
                                debounce={950}
                                onIonInput={
                                    (e: TextareaCustomEvent) => {
                                        addMessage(e.detail.value!)
                                    }}
                            ></IonTextarea>
                        </IonCardContent>
                    </IonCard>

                    <IonGrid style={{ marginRight: '10%', marginLeft: '10%' }}>
                        <IonRow className="ion-justify-content-center">
                            <IonCol size="5" className="ion-text-center">
                                <IonButton
                                    disabled={!isValid}
                                    size="large"
                                    fill="clear"
                                    color={'primary'}
                                    onClick={() => { openStellarPayQRModal() }}
                                >
                                    <IonIcon color="primary" icon={qrCodeOutline}></IonIcon>
                                </IonButton>
                                <br />
                                <IonLabel className="ion-text-center" >
                                    {t('stellarPay.page.BTN-QR-LABEL')}
                                </IonLabel>
                            </IonCol>

                            <IonCol size="5" className="ion-text-center">
                                <IonButton
                                    disabled={!isValid}
                                    size="large"
                                    fill="clear"
                                    color={"primary"}
                                    onClick={() => { handleShareStellarPayUri() }}>
                                    <IonIcon color="primary" icon={shareOutline}></IonIcon>
                                </IonButton>
                                <br />
                                <IonLabel className="ion-text-center">
                                    {t('stellarPay.page.BTN-SHARE-LABEL')}
                                </IonLabel>
                            </IonCol>
                        </IonRow>
                    </IonGrid>

                </IonContent>

            </IonPage>
        </form>
    )
}

const ReceiveStellarPayPage: React.FC = () => (
    <Provider store={store}>
        <Page />
    </Provider>
);

export default ReceiveStellarPayPage;